// from the mobiscroll docs https://mobiscroll.com/docs/react/getting-started/installation#use-the-components
// necessary because "For the components to work, you will also need to import the css file
// of the installed library under the node_modules folder (at @mobiscroll/react/dist/css/mobiscroll.min.css)."
import '../../../assets/styles/mobiscroll.css';
// eslint-disable-next-line no-restricted-imports
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import {
  Eventcalendar,
  localeDe,
  type MbscCalendarCellData,
  type MbscCalendarColor,
  type MbscCalendarEventData,
  type MbscCalendarInvalid,
  type MbscCellClickEvent,
  type MbscEventcalendarView,
  type MbscEventClickEvent,
  type MbscEventUpdatedEvent,
  type MbscEventUpdateEvent,
  type MbscPageChangeEvent,
  type MbscResource,
  setOptions,
} from '@mobiscroll/react';
import { isAfter, isBefore, min } from 'date-fns';
import { useCallback, useState } from 'react';
import { z } from 'zod';

import { type Zeitraum } from '../../../dtos';
import { localStorageApi, localStorageKeys } from '../../../utils';
import { type CalendarEvent, calendarResourceTypes } from './helperClasses';
import { type CalendarCollapsibleRow, calendarCollapsibleRowSchema } from './helperClasses/calendarCollapsibleRows';
import { type AbstractCalendarResource } from './helperClasses/CalendarResource';
import { LocationResource } from './helperClasses/CalendarResource/LocationResource';
import { DisplayCalendarEvent, DisplayCalendarResource } from './helperComponents';
import { AuslastungPercent } from './helperComponents/AuslastungPercent';
import { AuslastungStatistics } from './helperComponents/AuslastungStatistics';
import { ResourceCalendarHeader } from './ResourceCalendarHeader';

export type View = 'week' | 'day';

export const views = { WEEK: 'week', DAY: 'day' } as const;

type ResourceCalendarProps = {
  readonly resources: MbscResource[];
  readonly events: CalendarEvent[];
  readonly selectedEvents?: CalendarEvent[];
  readonly colors: MbscCalendarColor[];
  readonly timeRange: Zeitraum;
  readonly toggleExpansion: (collapsibleRow: CalendarCollapsibleRow, expand: boolean) => void;
  readonly initialDate: Date;
  readonly timeRangeChangeHandler: (timeRange: Zeitraum) => void;
  readonly cellClickHandler?: (event: MbscCellClickEvent) => void;
  readonly eventClickHandler?: (event: MbscEventClickEvent) => void;
  readonly initView?: View;
  readonly onViewChange?: (oldView: View, newView: View) => void;
  readonly onEventUpdate?: (event: MbscEventUpdateEvent) => boolean;
  readonly onEventUpdated?: (event: MbscEventUpdatedEvent) => void;
  readonly invalid?: MbscCalendarInvalid[];
};

const viewOptions: Record<View, MbscEventcalendarView['timeline']> = {
  week: {
    type: 'week',
    resolutionHorizontal: 'day',
    weekNumbers: true,
    columnWidth: 'medium',
    virtualScroll: false,
  },
  day: {
    type: 'day',
    columnWidth: 'xsmall',
    resolutionHorizontal: 'hour',
    timeCellStep: 60,
    weekNumbers: true,
    startTime: '06:00',
    endTime: '22:00',
  },
};

setOptions({
  locale: localeDe,
  theme: 'material',
  themeVariant: 'light',
});

const defaultWeekCount = 2;
const defaultDayCount = 7;

export const ResourceCalendar = ({
  resources,
  events,
  selectedEvents,
  colors,
  timeRange,
  toggleExpansion,
  initialDate,
  timeRangeChangeHandler,
  cellClickHandler,
  eventClickHandler,
  initView,
  onViewChange,
  onEventUpdate,
  onEventUpdated,
  invalid,
}: ResourceCalendarProps) => {
  const [view, setView] = useState<View>(initView ?? views.WEEK);

  const [weekCount, setWeekCount] = useState(
    view === views.WEEK
      ? (localStorageApi.get(localStorageKeys.RESOURCE_CALENDAR_WEEK_COUNT) ?? defaultWeekCount)
      : defaultDayCount,
  );
  const [selectedDate, setSelectedDate] = useState(initialDate);

  /**
   * Mobiscroll's Eventcalender callbacks like onPageChange or onPageLoading are implemented synchronously instead of asynchronously.
   * In consequence, calling a callback function (in this case, timeRangeChangeHandler) which would lead to a rerender of the parent
   * component, leads to cyclic render issues.
   * To prevent those cyclic render issues, we need to turn the sync function into an async function (so that it is executed after the render has finished).
   * This is done by using a setTimout with a timeout of zero.
   * Note: Cyclic render issues are only ever visible in local execution mode when React is using strict mode.
   */
  const onPageLoading = ({ firstDay, lastDay }: MbscPageChangeEvent): void => {
    setTimeout(() => {
      timeRangeChangeHandler({ start: firstDay, end: lastDay });
    }, 0);
  };

  const resourceRender = useCallback(
    (resource: AbstractCalendarResource) => (
      <DisplayCalendarResource resource={resource} year={timeRange.start.getFullYear()} />
    ),
    [timeRange],
  );
  const eventRender = useCallback(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- waiting for replacement of CalendarEvent type with real class
    (event: MbscCalendarEventData) => <DisplayCalendarEvent event={event.original as CalendarEvent} />,
    [],
  );

  const renderCell = useCallback(
    (args: MbscCalendarCellData) => {
      const { date, resource } = args;
      if (
        view !== 'week' ||
        !(resource instanceof LocationResource) ||
        resource.calendarResourceType !== calendarResourceTypes.SEMINARRAUM
      ) {
        return null;
      }

      if (isBefore(date, timeRange.start) || isAfter(date, timeRange.end)) {
        // happens when timeRange is initially not yet correctly set
        return null;
      }

      const isMonday = date.getDay() === 1;
      const isFriday = date.getDay() === 5;

      const auslastung = resource.seminarrundenAuslastung.find(
        (auslastung) => date >= auslastung.timeRange.start && date < auslastung.timeRange.end,
      );

      if (isMonday && auslastung !== undefined) {
        return <AuslastungStatistics auslastung={auslastung} />;
      }

      if (isFriday && auslastung !== undefined) {
        return <AuslastungPercent auslastung={auslastung} />;
      }
    },
    [timeRange.end, timeRange.start, view],
  );

  const handleViewChange = (newView: View): void => {
    setView((prev) => {
      if (onViewChange !== undefined) {
        onViewChange(prev, newView);
      }

      return newView;
    });

    const currentSelection = events.filter((event) => event.isCurrentSelection);
    if (newView === views.DAY && currentSelection.length > 0) {
      const start = min(currentSelection.map((event) => event.start));
      setSelectedDate(start);
    }
  };

  return (
    <Eventcalendar
      renderHeader={() => (
        <ResourceCalendarHeader
          weekCount={weekCount}
          onWeekCountChange={(value: number) => setWeekCount(value)}
          view={view}
          setView={handleViewChange}
        />
      )}
      colors={colors}
      data={events}
      resources={resources}
      selectedDate={selectedDate}
      onSelectedDateChange={(event) => setSelectedDate(z.instanceof(Date).parse(event.date))}
      selectedEvents={selectedEvents}
      locale={localeDe}
      onCellClick={cellClickHandler}
      onEventClick={eventClickHandler}
      onPageLoading={onPageLoading}
      onResourceExpand={(expandEvent): void =>
        toggleExpansion(calendarCollapsibleRowSchema.parse(expandEvent.resource), false)
      }
      onResourceCollapse={(expandEvent): void =>
        toggleExpansion(calendarCollapsibleRowSchema.parse(expandEvent.resource), true)
      }
      onEventUpdate={onEventUpdate}
      onEventUpdated={onEventUpdated}
      invalid={invalid}
      showEventTooltip={false}
      renderResource={resourceRender}
      renderTimelineEvent={eventRender}
      renderCell={renderCell}
      dragToResize
      dragToMove
      dragBetweenResources
      theme="material"
      themeVariant="light"
      view={{
        timeline: {
          ...viewOptions[view],
          size: weekCount,
        },
      }}
    />
  );
};
